import adminApi from '../utils/adminApi';

export const cloudinaryService = {
  /**
   * Delete a single image from Cloudinary
   * @param {string} imageUrl - The Cloudinary URL of the image to delete
   * @returns {Promise} Response from the deletion API
   */
  deleteImage: async (imageUrl) => {
    try {
      console.log('🗑️ Cloudinary Service: Deleting image:', imageUrl);
      const response = await adminApi.post('/api/admin/images/delete', { 
        image_url: imageUrl 
      });
      
      if (response.data.success) {
        console.log('✅ Cloudinary deletion successful:', response.data.message);
      } else {
        console.warn('⚠️ Cloudinary deletion issue:', response.data.message);
      }
      
      return response.data;
    } catch (error) {
      console.error('❌ Error deleting image from Cloudinary:', error);
      throw error;
    }
  },

  /**
   * Upload a PDF file to Cloudinary using signed upload
   * @param {File} file - The PDF file to upload
   * @param {string} category - Asset category for folder organization
   * @returns {Promise} Upload result with secure_url
   */
  uploadPdf: async (file, category = 'vehicle') => {
    try {
      console.log('📄 Cloudinary Service: Starting PDF upload:', file.name);
      
      // Determine folder path based on category
      const folderMapping = {
        'vehicle': 'The Haute Circle/Vehicles',
        'jet': 'The Haute Circle/Private Jets',
        'yacht': 'The Haute Circle/Yachts',
        'property': 'The Haute Circle/Vacation Homes'
      };
      
      const baseFolder = folderMapping[category] || 'The Haute Circle/Other';
      const fullFolder = `${baseFolder}/brochures`;
      
      // Get signed upload parameters from backend with the correct folder
      const signResponse = await adminApi.post('/api/admin/upload/pdf', { folder: fullFolder });
      if (!signResponse.data.success) {
        throw new Error('Failed to get upload signature');
      }
      
      const { upload_params, signed_params, signature, api_key, upload_url } = signResponse.data;
      
      console.log('🔐 Signed parameters:', signed_params);
      console.log('🔐 All parameters:', upload_params);
      console.log('📂 Final folder:', fullFolder);
      
      // Create a new FormData object
      const formData = new FormData();
      
      // Add the file first
      formData.append('file', file);
      
      // Add authentication
      formData.append('api_key', api_key);
      formData.append('signature', signature);
      
      // Add ONLY the signed parameters (in alphabetical order for Cloudinary)
      formData.append('allowed_formats', signed_params.allowed_formats);
      formData.append('folder', signed_params.folder);
      formData.append('tags', signed_params.tags);
      formData.append('timestamp', signed_params.timestamp.toString());
      formData.append('type', signed_params.type);
      formData.append('unique_filename', signed_params.unique_filename);
      formData.append('use_filename', signed_params.use_filename);
      
      // Add the resource_type (required for raw uploads but not signed)
      formData.append('resource_type', upload_params.resource_type);
      
      // Upload to Cloudinary
      console.log('📤 Uploading to:', upload_url);
      
      // Upload to Cloudinary using fetch (properly handling response consumption)
      const uploadResponse = await fetch(upload_url, {
        method: 'POST',
        body: formData
      });
      
      // Parse response content ONCE to avoid "Body disturbed" error
      let result;
      try {
        const responseText = await uploadResponse.text();
        
        if (!uploadResponse.ok) {
          console.error('❌ Upload error response:', responseText);
          throw new Error(`Upload failed: ${uploadResponse.status} - ${responseText}`);
        }
        
        // Parse as JSON after confirming success
        result = JSON.parse(responseText);
        console.log('✅ PDF uploaded successfully:', result.secure_url);
        
      } catch (jsonError) {
        console.error('❌ Error parsing response:', jsonError);
        throw new Error(`Upload response parsing failed: ${jsonError.message}`);
      }
      
      return result;
      
    } catch (error) {
      console.error('❌ Error uploading PDF to Cloudinary:', error);
      throw error;
    }
  },

  /**
   * Upload an image to Cloudinary using signed upload
   * @param {File} file - The image file to upload
   * @param {string} category - Asset category for folder organization
   * @param {string} imageType - 'exterior' or 'interior'
   * @returns {Promise} Upload result with secure_url
   */
  uploadImage: async (file, category = 'vehicle', imageType = 'exterior') => {
    try {
      console.log('🖼️ Cloudinary Service: Starting image upload:', file.name);
      
      // Get signed upload parameters from backend with the correct folder
      const signResponse = await adminApi.post('/api/admin/upload/image', { folder: fullFolder });
      if (!signResponse.data.success) {
        throw new Error('Failed to get upload signature');
      }
      
      const { upload_params, signed_params, signature, api_key, upload_url } = signResponse.data;
      
      console.log('🔐 Signed parameters:', signed_params);
      console.log('🔐 All parameters:', upload_params);
      console.log('📂 Final folder:', fullFolder);
      
      // Create FormData with only signed parameters
      const formData = new FormData();
      formData.append('file', file);
      formData.append('api_key', api_key);
      formData.append('signature', signature);
      
      // Add ONLY the signed parameters
      formData.append('timestamp', signed_params.timestamp.toString());
      formData.append('folder', signed_params.folder);
      formData.append('tags', signed_params.tags);
      formData.append('allowed_formats', signed_params.allowed_formats);
      formData.append('type', signed_params.type);
      
      // Add the resource_type (required for image uploads but not signed)
      formData.append('resource_type', upload_params.resource_type);
      
      // Upload to Cloudinary using fetch (properly handling response consumption)
      const uploadResponse = await fetch(upload_url, {
        method: 'POST',
        body: formData
      });
      
      // Parse response content ONCE to avoid "Body disturbed" error
      let result;
      try {
        const responseText = await uploadResponse.text();
        
        if (!uploadResponse.ok) {
          console.error('❌ Upload error response:', responseText);
          throw new Error(`Upload failed: ${uploadResponse.status} - ${responseText}`);
        }
        
        // Parse as JSON after confirming success
        result = JSON.parse(responseText);
        console.log('✅ Image uploaded successfully:', result.secure_url);
        
      } catch (jsonError) {
        console.error('❌ Error parsing response:', jsonError);
        throw new Error(`Upload response parsing failed: ${jsonError.message}`);
      }
      
      return result;
      
    } catch (error) {
      console.error('❌ Error uploading image to Cloudinary:', error);
      throw error;
    }
  },

  /**
   * Generate a secure signed URL for PDF viewing
   * @param {string} pdfUrl - The Cloudinary PDF URL
   * @returns {Promise} Signed URL for secure access
   */
  generateSecurePdfUrl: async (pdfUrl) => {
    try {
      console.log('🔐 Cloudinary Service: Generating secure PDF URL');
      
      const response = await adminApi.post('/api/admin/generate-pdf-url', { 
        image_url: pdfUrl 
      });
      
      if (response.data.success) {
        console.log('✅ Secure PDF URL generated');
        return response.data.signed_url;
      } else {
        throw new Error('Failed to generate secure URL');
      }
      
    } catch (error) {
      console.error('❌ Error generating secure PDF URL:', error);
      throw error;
    }
  },

  /**
   * Delete multiple images from Cloudinary
   * @param {string[]} imageUrls - Array of Cloudinary URLs to delete
   * @returns {Promise} Response with deletion results
   */
  bulkDeleteImages: async (imageUrls) => {
    try {
      console.log('🗑️ Cloudinary Service: Bulk deleting images:', imageUrls.length);
      const response = await adminApi.delete('/api/admin/images/bulk-delete', { 
        data: imageUrls 
      });
      
      console.log(`✅ Bulk deletion completed: ${response.data.summary.successful}/${response.data.summary.total} successful`);
      return response.data;
    } catch (error) {
      console.error('❌ Error bulk deleting images from Cloudinary:', error);
      throw error;
    }
  },

  /**
   * Delete images related to an asset
   * @param {Object} asset - Asset object with images
   * @returns {Promise} Array of deletion results
   */
  deleteAssetImages: async (asset) => {
    const imagesToDelete = [];
    
    // Collect all images from the asset
    if (asset.images && Array.isArray(asset.images)) {
      imagesToDelete.push(...asset.images);
    }
    if (asset.exterior_images && Array.isArray(asset.exterior_images)) {
      imagesToDelete.push(...asset.exterior_images);
    }
    if (asset.interior_images && Array.isArray(asset.interior_images)) {
      imagesToDelete.push(...asset.interior_images);
    }
    if (asset.brochure_url) {
      imagesToDelete.push(asset.brochure_url);
    }
    
    // Filter only Cloudinary URLs
    const cloudinaryImages = imagesToDelete.filter(url => 
      url && url.includes('cloudinary.com')
    );
    
    if (cloudinaryImages.length === 0) {
      console.log('ℹ️ No Cloudinary images found for asset:', asset.name);
      return [];
    }
    
    console.log(`🗑️ Deleting ${cloudinaryImages.length} Cloudinary images for asset: ${asset.name}`);
    
    // Delete each image individually for better error handling
    const results = [];
    for (const imageUrl of cloudinaryImages) {
      try {
        const result = await cloudinaryService.deleteImage(imageUrl);
        results.push({ url: imageUrl, ...result });
      } catch (error) {
        console.error('❌ Failed to delete image:', imageUrl, error);
        results.push({ 
          url: imageUrl, 
          success: false, 
          message: error.message 
        });
      }
    }
    
    return results;
  },

  /**
   * Delete images related to a curation package
   * @param {Object} packageData - Package object with images
   * @returns {Promise} Array of deletion results
   */
  deletePackageImages: async (packageData) => {
    const imagesToDelete = [];
    
    // Collect package hero image
    if (packageData.package_image_url) {
      imagesToDelete.push(packageData.package_image_url);
    }
    
    // Collect vehicle images
    if (packageData.vehicles && Array.isArray(packageData.vehicles)) {
      for (const vehicle of packageData.vehicles) {
        if (vehicle.images && Array.isArray(vehicle.images)) {
          imagesToDelete.push(...vehicle.images);
        }
      }
    }
    
    // Collect brochure
    if (packageData.brochure_url) {
      imagesToDelete.push(packageData.brochure_url);
    }
    
    // Filter only Cloudinary URLs
    const cloudinaryImages = imagesToDelete.filter(url => 
      url && url.includes('cloudinary.com')
    );
    
    if (cloudinaryImages.length === 0) {
      console.log('ℹ️ No Cloudinary images found for package:', packageData.name);
      return [];
    }
    
    console.log(`🗑️ Deleting ${cloudinaryImages.length} Cloudinary images for package: ${packageData.name}`);
    
    // Delete each image individually for better error handling
    const results = [];
    for (const imageUrl of cloudinaryImages) {
      try {
        const result = await cloudinaryService.deleteImage(imageUrl);
        results.push({ url: imageUrl, ...result });
      } catch (error) {
        console.error('❌ Failed to delete image:', imageUrl, error);
        results.push({ 
          url: imageUrl, 
          success: false, 
          message: error.message 
        });
      }
    }
    
    return results;
  }
};